import { useContext, useEffect } from "react";
import { SsrRequestContext } from "@/lib/ssr-bootstrap";
import { applyPageMeta, type PageMeta } from "@/lib/seo";
import { applyResolvedPublicMetadata, loadResolvedPublicMetadata } from "@/lib/resolved-public-metadata";
import { BRAND_TRANSITION_PATH } from "@/lib/brand-transition-meta";
import { isNonProductionSeoHost } from "@/lib/seo-recovery-policy.mjs";

/**
 * Historical identity is confined to this exact source. The frozen OFF build
 * predates pageIdentity support, so reconcile only this page's display fields
 * after the ordinary metadata writer has applied the server-owned SEO policy.
 */
export function useBrandTransitionMeta(meta: PageMeta) {
  const request = useContext(SsrRequestContext);
  if (request) request.pageMeta = { ...meta, canonical: request.canonical || meta.canonical };
  useEffect(() => {
    let cancelled = false;
    if (window.location.pathname !== BRAND_TRANSITION_PATH) return;
    void loadResolvedPublicMetadata(BRAND_TRANSITION_PATH).then(decision => {
      if (cancelled || window.location.pathname !== BRAND_TRANSITION_PATH || !decision ||
          decision.contentIdentity !== `static-page:${BRAND_TRANSITION_PATH}`) return;
      const preview = isNonProductionSeoHost(window.location.hostname);
      applyPageMeta({ ...meta, canonical: decision.canonical, noIndex: preview || meta.noIndex });
      // A public-origin source decision cannot upgrade a preview host. Keep the
      // same restricting host policy as the initial-response delivery guard.
      if (preview) applyResolvedPublicMetadata({ ...decision, indexable: false, robots: "noindex, follow" });
      // Canonical/hreflang still come solely from the server-owned projection.
      document.title = meta.title!;
      for (const [selector, content] of [
        ['meta[property="og:title"]', meta.title],
        ['meta[name="twitter:title"]', meta.title],
        ['meta[name="description"]', meta.description],
        ['meta[property="og:description"]', meta.description],
        ['meta[name="twitter:description"]', meta.description],
      ]) {
        const node = document.querySelector(selector!);
        if (node && content) node.setAttribute("content", content);
      }
    }).catch(error => console.error("Transition metadata projection unavailable", error));
    return () => { cancelled = true; };
  }, [meta]);
}
